<template>
  <a-modal
    class="template-add-edit-modal"
    :title="modalTitle"
    :visible="visible"
    :maskClosable="false"
    centered
    @cancel="handleCancel"
  >
    <!-- custom footer -->
    <template v-slot:footer>
      <a-button key="submit" class="delete-btn" @click="handleCurrentPage">{{
        currentPage === 1 ? $t("TP_Modal_Next") :  $t("TP_Modal_Prev") 
      }}</a-button>
      <a-button key="back" @click="handleCancel">{{$t("Common_Cancel")}}</a-button>
    </template>

    <!-- contents -->
    <TemplateEditPage1
      v-if="currentPage === 1"
      :mode="mode"
      :templateInfo="templateInfo"
      :saveInputValue="saveInputValue"
      :currentPage="currentPage"
    />

    <TemplateEditPage2
      v-if="currentPage === 2"
      :mode="mode"
      :templateNo="templateNo"
      :templateInfo="templateInfo"
      :saveInputValue="saveInputValue"
      :images="images"
      :getImages="getImages"
      :deleteImages="deleteImages"
      :currentPage="currentPage"
      :toggleModal="toggleModal"
    />
  </a-modal>
</template>

<script>
import {
  onBeforeUnmount,
  onBeforeUpdate,
  onMounted,
  onUpdated,
  reactive,
  ref,
  renderSlot,
  toRefs,
  watch,
  computed,
  onUnmounted,
} from "vue";
import { useStore } from "vuex";
import axios from "axios";
import { apiUrl } from "@/utils/common";
import TemplateEditPage1 from "@/components/sideComponents/TemplateEditPage1";
import TemplateEditPage2 from "@/components/sideComponents/TemplateEditPage2";
import { useI18n } from "vue-i18n";

export default {
  name: "TemplateAddEditModal",
  props: {
    mode: String,
    visible: Boolean,
    templateInfo: Object,
    toggleModal: Function,
  },
  components: {
    TemplateEditPage1,
    TemplateEditPage2,
  },
  setup(props) {
    const store = useStore();
    const templateInputValues = computed(
      () => store.getters["template/GET_INPUT_VALUES"]
    );
    const { t } = useI18n();
    const state = reactive({
      loading: false,
      currentPage: 1,
      templateNo: props.templateInfo.templateNo,
      images: props.templateInfo.imageList ? props.templateInfo.imageList : [],
      uploadedList: [],
      enable: true,
    });

    const modalTitle =
      props.mode === "edit"
        ? t("TP_Modal_Title1")
        : t("TP_Modal_Title2");

    onMounted(() => {
      const {
        body,
        phishingHtml1,
        phishingCss1,
        phishingJs1,
        phishingHtml2,
        phishingCss2,
        phishingJs2,
        resultScreenHtml,
        resultScreenCss,
        resultScreenJs,

        templateNo,
        templateName,
        templatePublic,
        autoUpdate,
        category,
        examType,
        isEnable,
        isPublic,
        language,
        phishingLevel,
        screenEnable,
        sendEmail,
        sendName,
        subject,
      } = props.templateInfo;

      const message = t("TP_Modal_html");

      let templateCode = [
        {
          html: `<div style="color: #333333; font-family: 'Malgun Gothic', sans-serif; padding: 0; text-align: left">
  ${message}<br />
</div>`,
        },
      ];

      let resultCode = {
        html: "",
        css: "",
        js: "",
      };

      if (props.mode === "add") {
        window.addEventListener("beforeunload", insertCancelTemplate);
      } else if (props.mode === "edit") {
        templateCode[0]["html"] = body;
        if (examType === 4) {
          templateCode[1] = {
            html: phishingHtml1,
            css: phishingCss1,
            js: phishingJs1,
          };
          templateCode[2] = {
            html: phishingHtml2,
            css: phishingCss2,
            js: phishingJs2,
          };
        }

        resultCode = {
          html: resultScreenHtml,
          css: resultScreenCss,
          js: resultScreenJs,
        };
      }

      // 템플릿 데이터를 input 기본값으로 설정
      saveInputValue({
        templateNo: templateNo,
        templateName: templateName ? templateName : "",
        templatePublic: templatePublic !== undefined ? templatePublic : "",
        autoUpdate: autoUpdate !== undefined ? autoUpdate : "",
        isPublic: isPublic !== undefined ? isPublic : 1,
        category: category !== undefined ? category : "",
        language: language !== undefined ? language : "",
        isEnable: isEnable !== undefined ? isEnable : 1,
        examType: examType !== undefined ? examType : "",
        subject: subject ? subject : "",
        sendName: sendName ? sendName : "",
        sendEmail: sendEmail ? sendEmail : "",
        phishingLevel: phishingLevel !== undefined ? phishingLevel : 1,
        screenEnable: screenEnable !== undefined ? screenEnable : 1,
        templates: templateCode,
        templateResult: resultCode,
      });
    });

    onBeforeUnmount(() => {
      window.removeEventListener("beforeunload", insertCancelTemplate);
    });

    onUnmounted(() => {
      store.dispatch("template/INIT_INPUT_VALUE");
    });

    // 저장하지 않고 페이지 새로고침 시, 생성된 템플릿 번호 삭제
    const insertCancelTemplate = async () => {
      try {
        const response = await axios.delete(apiUrl + "Template/InsertCancle", {
          params: { templateNo: state["templateNo"] },
        });
      } catch (error) {
        console.log("insert cancel templateNo", error);
      }
    };

    // 필수 항목 입력 여부 체크
    const checkValid = (compareArray) => {
      let enableArray = [];
      compareArray.map((key) => {
        const value = templateInputValues.value[key];
        if (!!value || value === 0) {
          enableArray.push(key);
        }
      });

      state["enable"] = enableArray.length === compareArray.length;
    };

    // 이전/다음 버튼 이벤트
    const handleCurrentPage = () => {
      const targetArray = [
        "templateName",
        "templatePublic",
        "autoUpdate",
        "isPublic",
        "category",
        "language",
        "examType",
        "subject",
        "sendName",
        "sendEmail",
      ];
      checkValid(targetArray);

      if (state["enable"]) {
        state["currentPage"] = state["currentPage"] === 1 ? 2 : 1;
      }
    };

    // 입력 값 스토어에 저장
    const saveInputValue = (inputData) => {
      store.dispatch("template/SET_INPUT_VALUE", inputData);
    };

    // 이미지 업로드/삭제 버튼 클릭 시 이미지 리스트 변경
    const getImages = (type, value) => {
      if (type === "upload") {
        state["uploadedList"] = value;
        state["images"] = value;
      } else {
        state["images"] = value;
      }
    };

    // 업로드된 이미지 삭제
    const deleteImages = (origin, current, newImages, type) => {
      // origin: 기존 이미지 리스트, current: 현재 이미지 리스트, newImages: 이미지 목록에 보여줄 리스트, type: 비교 기준 설정(1: origin, 2: current)
      const originNoArray =
        origin && origin.length > 0 ? origin.map((item) => item.imageNo) : [];
      const currentNoArray =
        current && current.length > 0
          ? current.map((item) => item.imageNo)
          : [];
      const mainArray = type === 1 ? originNoArray : currentNoArray;
      const subArray = type === 1 ? currentNoArray : originNoArray;

      if (mainArray.length > 0) {
        const differentNoArray = mainArray.filter(
          (element) => subArray.indexOf(element) < 0
        );
        if (differentNoArray.length > 0) {
          const fileParams = {
            templateNo: state.templateNo,
            imageNoArray: JSON.stringify(differentNoArray),
          };
          // await axios.delete(apiUrl + "Template/UploadImage", { params: fileParams });
        }

        // 이미지 리스트 업데이트
        state["images"] = newImages;
      }
    };

    // 취소 버튼 이벤트
    const handleCancel = async () => {
      if (props.mode === "add") {
        const origin = [];
        const current = state.uploadedList;
        deleteImages(origin, current, origin, 2);
        insertCancelTemplate();
      } else if (props.mode === "edit") {
        const origin = props.templateInfo.imageList;
        const current = state.images;
        deleteImages(origin, current, origin, 2);
      }

      props.toggleModal();
    };

    return {
      ...toRefs(state),
      modalTitle,
      handleCurrentPage,
      handleCancel,
      deleteImages,
      saveInputValue,
      getImages,
    };
  },
};
</script>